<template>
    <page-controls>
        <template v-slot:left>
            <div class="grow @3xl/controls:max-w-md">
                <vx-filterable :active="active" @toggle="toggleFilterable" data-test="filters">
                    <template v-slot:placeholder>
                        <span class="mr-2">Find</span>
                        <vx-badge
                            v-if="filtersCount > 0"
                            data-test="reset"
                            :borders="false"
                            size="xs"
                            @click.stop="reset"
                        >
                            {{ pluralized('filter', filtersCount, true) }}
                            <font-awesome-icon widthAuto :icon="faCircleXmark" />
                        </vx-badge>
                    </template>
                    <template v-slot:form>
                        <form @submit.prevent="apply" class="flex flex-col gap-4">
                            <div>
                                <filter-label :canReset="!!filters.query" @reset="filters.query = undefined">
                                    Keyword Search
                                </filter-label>
                                <vx-input
                                    type="text"
                                    data-test="query"
                                    placeholder="Search"
                                    maxlength="80"
                                    size="lg"
                                    v-model="filters.query"
                                >
                                    <template v-slot:right>
                                        <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                                    </template>
                                </vx-input>
                            </div>

                            <div class="flex gap-2 pt-4">
                                <vx-button
                                    data-test="cancel"
                                    type="button"
                                    color="muted-light"
                                    size="lg"
                                    @click="cancel"
                                >
                                    Cancel
                                </vx-button>
                                <vx-button data-test="apply" type="submit" size="lg" block>
                                    Apply
                                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                                </vx-button>
                            </div>
                        </form>
                    </template>
                </vx-filterable>
            </div>
            <div class="flex w-full gap-2 @xl/controls:w-auto">
                <vx-button-group size="lg" class="w-full">
                    <vx-select
                        v-model="filters.sort_field"
                        @update:modelValue="apply"
                        :options="[
                            {
                                value: 'name',
                                label: 'Sort by: Name',
                            },
                            {
                                value: 'group_id',
                                label: 'Sort by: Group ID',
                            },
                        ]"
                    ></vx-select>
                    <vx-button
                        @click.prevent="
                            filters.sort_dir = filters.sort_dir === 'asc' ? 'desc' : 'asc';
                            apply();
                        "
                        type="button"
                        color="white"
                        shadow
                    >
                        <font-awesome-icon
                            widthAuto
                            :icon="props.modelValue.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                        ></font-awesome-icon>
                    </vx-button>
                </vx-button-group>
            </div>
        </template>
        <template v-slot:right>
            <div v-if="isAdminOrOwner" class="flex items-center gap-2">
                <vx-dropdown placement="right">
                    <vx-button data-test="dropdown" type="button" color="muted-dark" size="lg">
                        <font-awesome-icon widthAuto :icon="faEllipsis" />
                    </vx-button>

                    <template v-slot:items>
                        <vx-dropdown-item
                            :href="
                                currentOrgUnit
                                    ? `/settings/teams/${props.teamId}#/current-org-unit-details`
                                    : `/settings/teams/${props.teamId}/org-units/${props.orgUnit.id}`
                            "
                            data-test="edit"
                        >
                            Edit OU
                            <font-awesome-icon widthAuto :icon="faFilePen"></font-awesome-icon>
                        </vx-dropdown-item>
                        <vx-dropdown-item
                            v-if="onCorporate()"
                            @click="bulkGroupsModal = true"
                            data-test="move-all-groups"
                        >
                            Move All Groups
                            <font-awesome-icon widthAuto :icon="faFolderGrid"></font-awesome-icon>
                        </vx-dropdown-item>
                    </template>
                </vx-dropdown>
                <vx-button @click="addGroupModal = true" size="lg" block>
                    Add Group <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
                <org-unit-add-groups-modal
                    v-model:visible="addGroupModal"
                    :teamId="props.teamId"
                    :orgUnit="props.orgUnit"
                    @added="emit('added', $event)"
                ></org-unit-add-groups-modal>
            </div>

            <org-unit-bulk-move-groups-modal
                v-model:visible="bulkGroupsModal"
                :teamId="props.teamId"
                :sourceOrgUnit="props.orgUnit"
                @completed="reset()"
            ></org-unit-bulk-move-groups-modal>
        </template>
    </page-controls>
</template>

<script setup>
import {
    faArrowDownWideShort,
    faArrowUpWideShort,
    faCircleCheck,
    faMagnifyingGlass,
    faCircleXmark,
    faCirclePlus,
    faEllipsis,
    faFilePen,
    faFolderGrid,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxButtonGroup,
    VxDropdown,
    VxDropdownItem,
    VxFilterable,
    VxInput,
    VxSelect,
} from '@voxie/frontend-components';
import { computed, nextTick, ref, watch } from 'vue';
import PageControls from '~/components/general/PageControls.vue';
import OrgUnitBulkMoveGroupsModal from '~/components/settings/teams/org-units/groups/modals/OrgUnitBulkMoveGroupsModal.vue';
import OrgUnitAddGroupsModal from '~/components/settings/teams/org-units/groups/modals/OrgUnitAddGroupsModal.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import FilterLabel from '~/components/general/FilterLabel.vue';
import { pluralized } from '~/components/filters';

const isAdminOrOwner = Spark.isAdminOrOwner;
const { onCorporate, currentOrgUnit } = useCurrentGroup();

const filters = ref({});
const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    orgUnit: {
        type: Object,
        required: true,
    },
    modelValue: {
        type: Object,
        required: true,
    },
});

watch(
    () => props.modelValue,
    (modelValue) => {
        filters.value = { ...modelValue };
    },
    {
        deep: true,
        immediate: true,
    }
);

const emit = defineEmits(['update:modelValue', 'apply', 'reset', 'added']);
const addGroupModal = ref(false);

const bulkGroupsModal = ref(false);

const active = ref(false);

const toggleFilterable = (event) => {
    active.value = event;
};

const filtersCount = computed(() => (props.modelValue.query ? 1 : 0));

const reset = () => {
    active.value = false;
    emit('reset');
};

const cancel = async () => {
    filters.value = { ...props.modelValue };
    active.value = false;
};

const apply = async () => {
    active.value = false;
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
};
</script>
